<script setup>
// Imports
// Components imports

// Constants
import DomainContacts from 'pages/customer/components/DomainContacts.vue'
import RegisterDomain from 'pages/customer/components/RegisterDomain.vue'
import WebhostingPackages from 'pages/customer/components/WebhostingPackages.vue'
import { getProductDiscount, getProductSalesPrice, getProductSalesPriceWithDiscount, numberFormat } from 'src/utils/utils.js'

const api = useFeathers()
const router = useRouter()
const route = useRoute()
const { t } = useI18n({ useScope: 'global' })

// Constant declarations
const $q = useQuasar()

// Component props

// Stores
const authStore = useAuthStore()

// Refs
const componentLoaded = ref(false)
const step = ref(1)
const stepper = ref()
const isSubmitted = ref(false)
const acceptedTerms = ref(false)
const webhostingPackages = ref([])
const webhostingAccounts = ref([])
const defaultContract = ref(null)
const { selectedCustomerInfo } = storeToRefs(authStore)

const webhostingAccount = ref(null)
const webhostingAccountLabel = ref(null)
const newWebhostingProduct = ref(null)
const newWebhostingPackage = ref(null)
const newDomainSelectedProduct = ref(null)
const newDomainSelectedExtension = ref(null)
const newDomainName = ref(null)
const newDomainIsDomainFree = ref(null)
const newDomainIsDomainTransferable = ref(null)
const newDomainTransferCode = ref(null)
const newDomainFreeSuggestedTlds = ref([])
const newDomainUseOwnerContactForAll = ref(false)
const newDomainOwnerContact = ref(null)
const newDomainTechnicalContact = ref(null)
const newDomainAdministrativeContact = ref(null)
const newDomainBillingContact = ref(null)

const newWebhostingSuccess = ref(null)
const newDomainSuccess = ref(null)

// Inject
const reloadComponent = inject('reloadComponent')

// Computed properties

// Methods
const updateWebhostingAccountLabel = (label) => {
  webhostingAccountLabel.value = label
}

const onSubmit = async () => {
  isSubmitted.value = true
  let transferCodeInvalid = false

  // First create a new domain when 'new domain' is selected
  const domainData = {
    customer: selectedCustomerInfo.value._id,
    product: newDomainSelectedProduct.value._id,
    type: newDomainIsDomainFree.value ? 'register' : 'transfer',
    domain: newDomainName.value,
    tld: newDomainSelectedExtension.value,
    ownerContact: newDomainOwnerContact.value,
    webhosting: ![null, 'new', 'none'].includes(webhostingAccount.value) ? webhostingAccount.value : null,
  }
  if (newDomainAdministrativeContact.value) {
    domainData.administrativeContact = newDomainAdministrativeContact.value
  }
  if (newDomainTechnicalContact.value) {
    domainData.technicalContact = newDomainTechnicalContact.value
  }
  if (newDomainBillingContact.value) {
    domainData.billingContact = newDomainBillingContact.value
  }
  if (newDomainTransferCode.value && !newDomainIsDomainFree.value) {
    domainData.eppCode = newDomainTransferCode.value
  }

  const createDomain = await api
    .service('domains')
    .service.createFull(domainData)
    .catch((error) => {
      if (error.errors?.errorCode && error.errors.errorCode === 'NEW_FULL_DOMAIN_TRANSFER_CODE_INVALID') {
        $q.notify({
          type: 'negative',
          progress: true,
          message: t('Customer.New-domain.The transfer code is invalid'),
          html: true,
        })
        transferCodeInvalid = true
      }
      newDomainSuccess.value = false
      newWebhostingSuccess.value = false // Creating domain failed, it will never make a hosting anyway
      return false
    })
  if (createDomain) {
    newDomainSuccess.value = true
  }

  // Create the hosting when the new domain has been successfully created
  if (createDomain && webhostingAccount.value === 'new') {
    const hostingData = {
      customer: selectedCustomerInfo.value._id,
      webhostingPackage: newWebhostingPackage.value._id,
      domain: createDomain._id,
    }

    const createHosting = await api
      .service('webhosting')
      .service.createFull(hostingData)
      .catch(() => {
        newWebhostingSuccess.value = false
        return false
      })
    if (createHosting) {
      newWebhostingSuccess.value = true
    }
  }

  isSubmitted.value = false

  if (transferCodeInvalid) {
    step.value = 1
  } else {
    stepper.value.next()
  }
}

const getFilters = async (only = null) => {
  const filtersQuery = {
    only: ['webhosting-packages', 'webhosting'],
  }

  if (only) {
    filtersQuery.only = only
  }

  const filters = await api
    .service('domains')
    .filters(filtersQuery)
    .catch(() => {
      return null
    })

  if (!filters) {
    return
  }

  if (filters['webhosting-packages']) {
    filters['webhosting-packages'] = filters['webhosting-packages'].sort((a, b) => {
      const priceA = getProductSalesPrice(a.productInfo.salesPrices, 'year', 1)
      const priceB = getProductSalesPrice(b.productInfo.salesPrices, 'year', 1)
      return priceA - priceB
    })

    filters['webhosting-packages'].shift()

    webhostingPackages.value = filters['webhosting-packages']
  }

  if (filters.webhosting) {
    webhostingAccounts.value = filters['webhosting']
  }
}

// Watchers
watch(
  () => newDomainSelectedExtension.value,
  (newExtension, oldExtension) => {
    if (typeof oldExtension !== 'undefined' && oldExtension !== newExtension) {
      newDomainOwnerContact.value = null
      newDomainTechnicalContact.value = null
      newDomainAdministrativeContact.value = null
      newDomainBillingContact.value = null
    }
  },
  {
    immediate: true,
  },
)

watch(
  () => selectedCustomerInfo.value?._id,
  async (newCustomerId, oldCustomerId) => {
    if (!newCustomerId || !oldCustomerId || newCustomerId === oldCustomerId) {
      return
    }
    if (route.name === 'new-domain') {
      await router.push({ name: 'domains' })
    }
  },
)

// Lifecycle hooks
onMounted(async () => {
  if (!authStore.hasServicePermission('contacts', ['find_assigned']) || !authStore.hasServicePermission('contacts', ['get_assigned'])) {
    await router.push({ name: 'main' })
    return
  }

  await getFilters()

  const getCustomer = await api
    .service('customers')
    .get(selectedCustomerInfo.value._id)
    .catch(() => {
      return null
    })

  defaultContract.value = getCustomer.defaultContractInfo ? getCustomer.defaultContractInfo : null
  componentLoaded.value = true
})

// Expose
</script>

<template>
  <q-linear-progress v-if="!componentLoaded || webhostingPackages.length === 0" query class="absolute-top z-top" />
  <div class="flex q-px-sm q-px-md-md q-pt-md q-pb-md" v-show="componentLoaded && webhostingPackages.length > 0">
    <q-card class="full-width rounded-borders card-shadow" flat>
      <q-card-section class="q-py-sm q-py-sm-md q-px-none">
        <q-stepper ref="stepper" v-model="step" vertical alternative-labels color="primary" animated flat class="q-pa-none" active-icon="none">
          <!-- Step 1 (register new domain) -->
          <q-step :name="1" :title="$t('Customer.New-domain.Register domain')" icon="language" :done="step > 1">
            <register-domain
              :contract="defaultContract"
              v-model:selected-product="newDomainSelectedProduct"
              v-model:selected-extension="newDomainSelectedExtension"
              v-model:domain-name="newDomainName"
              v-model:is-domain-free="newDomainIsDomainFree"
              v-model:is-domain-transferable="newDomainIsDomainTransferable"
              v-model:transfer-code="newDomainTransferCode"
              v-model:free-suggested-tlds="newDomainFreeSuggestedTlds"
            />
          </q-step>

          <!-- Step 2 (link contacts to domain) -->
          <q-step :name="2" :title="$t('Customer.New-domain.Link contacts to domain')" icon="person" :done="step > 2">
            <domain-contacts
              :product="newDomainSelectedProduct"
              v-model:use-owner-contact-for-all="newDomainUseOwnerContactForAll"
              v-model:owner-contact="newDomainOwnerContact"
              v-model:technical-contact="newDomainTechnicalContact"
              v-model:administrative-contact="newDomainAdministrativeContact"
              v-model:billing-contact="newDomainBillingContact"
            />
          </q-step>

          <!-- Step 3 (link webhosting account) -->
          <q-step :name="3" :title="$t('Customer.New-domain.Select webhosting account')" icon="public" :done="step > 3">
            <q-card class="rounded-borders q-ml-sm max-width-px-550" bordered flat>
              <q-card-section class="q-px-md q-pb-md">
                <h5 class="no-margin">{{ $t('Customer.New-domain.Webhosting account') }}</h5>
                <p>{{ $t('Customer.New-domain.Link a webhosting account to your new domain') }}</p>
                <div class="flex column">
                  <div v-if="authStore.hasServicePermission('webhosting', ['createFull_assigned'])">
                    <q-radio
                      v-model="webhostingAccount"
                      val="new"
                      :label="$t('Customer.New-domain.Create new webhosting account')"
                      @update:model-value="updateWebhostingAccountLabel(null)"
                    />
                  </div>
                  <div>
                    <q-radio
                      v-model="webhostingAccount"
                      val="none"
                      :label="$t('Customer.New-domain.Do not link to webhosting account')"
                      @update:model-value="updateWebhostingAccountLabel(null)"
                    />
                  </div>
                  <q-separator class="q-my-sm" v-if="webhostingAccounts.length > 0" />
                  <div v-for="option in webhostingAccounts" :key="option.value">
                    <q-radio
                      v-model="webhostingAccount"
                      :val="option.value"
                      :label="option.label"
                      @update:model-value="updateWebhostingAccountLabel(option.label)"
                    />
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </q-step>

          <!-- Step 4 (webhosting packages) -->
          <q-step :name="4" :title="$t('Customer.New-domain.Choose package')" icon="view_module" :done="step > 4" v-if="webhostingAccount === 'new'">
            <webhosting-packages
              v-model="newWebhostingProduct"
              v-model:package="newWebhostingPackage"
              :subscription="null"
              :contract="defaultContract"
              :webhosting-packages="webhostingPackages"
              :small-padding="true"
              :always-show-buttons="true"
              :hide-unselected-packages="false"
              :card-shadow="false"
              :show-most-chosen="true"
            />
          </q-step>

          <!-- Step 4 or step 5 (overview) -->
          <q-step
            :name="webhostingAccount === 'new' ? 5 : 4"
            :title="$t('Customer.New-domain.Overview')"
            icon="view_list"
            :done="webhostingAccount === 'new' ? step > 5 : step > 4"
          >
            <q-card class="rounded-borders q-ml-sm max-width-px-550" bordered flat>
              <q-card-section class="q-px-sm q-px-md-md q-pb-md text-center">
                <div class="font-size-rem-13 flex flex-center">
                  <q-icon name="o_check" class="text-primary q-mr-sm" size="25px" />
                  {{ newDomainName }}.{{ newDomainSelectedExtension }}
                </div>
                <div class="q-mt-md text-primary font-size-rem-20">
                  {{
                    numberFormat(
                      useUserStore().currentRegionalSettings.lang,
                      getProductSalesPriceWithDiscount(
                        null,
                        defaultContract,
                        getProductSalesPrice(newDomainSelectedProduct.salesPrices, 'year', 1),
                        newDomainSelectedProduct._id,
                      ),
                      true,
                    )
                  }}
                </div>
                <div class="text-primary q-mt-sm">{{ $t('Customer.New-domain.per year') }}</div>
                <div
                  class="text-primary"
                  v-if="
                    getProductDiscount(
                      null,
                      defaultContract,
                      getProductSalesPrice(newDomainSelectedProduct.salesPrices, 'year', 1),
                      newDomainSelectedProduct._id,
                    )
                  "
                >
                  {{ $t('Customer.New-domain.incl') }}
                  {{
                    numberFormat(
                      useUserStore().currentRegionalSettings.lang,
                      getProductDiscount(
                        null,
                        defaultContract,
                        getProductSalesPrice(newDomainSelectedProduct.salesPrices, 'year', 1),
                        newDomainSelectedProduct._id,
                      ),
                      true,
                    )
                  }}
                  {{ $t('Customer.New-domain.discount') }}
                </div>
                <q-separator class="q-mt-md" v-if="webhostingAccount !== 'none'" />
                <div class="font-size-rem-13 q-mt-md" v-if="webhostingAccount !== 'none'">
                  {{ $t('Customer.New-domain.Linked hosting') }}
                </div>
                <template v-if="webhostingAccount === 'new'">
                  <div class="q-mt-md">{{ newWebhostingPackage.name }}</div>
                  <div class="q-mt-md text-primary font-size-rem-20">
                    {{
                      numberFormat(
                        useUserStore().currentRegionalSettings.lang,
                        getProductSalesPriceWithDiscount(
                          null,
                          defaultContract,
                          getProductSalesPrice(newWebhostingPackage.productInfo.salesPrices, 'year', 1),
                          newWebhostingProduct,
                        ),
                        true,
                      )
                    }}
                  </div>
                  <div class="text-primary q-mt-sm">{{ $t('Customer.New-domain.per year') }}</div>
                  <div
                    class="text-primary"
                    v-if="
                      getProductDiscount(
                        null,
                        defaultContract,
                        getProductSalesPrice(newWebhostingPackage.productInfo.salesPrices, 'year', 1),
                        newWebhostingProduct,
                      )
                    "
                  >
                    {{ $t('Customer.New-domain.incl') }}
                    {{
                      numberFormat(
                        useUserStore().currentRegionalSettings.lang,
                        getProductDiscount(
                          null,
                          defaultContract,
                          getProductSalesPrice(newWebhostingPackage.productInfo.salesPrices, 'year', 1),
                          newWebhostingProduct,
                        ),
                        true,
                      )
                    }}
                    {{ $t('Customer.New-domain.discount') }}
                  </div>
                </template>
                <template v-else-if="webhostingAccount !== 'none'">
                  <div class="q-mt-md">{{ webhostingAccountLabel }}</div>
                </template>
                <q-separator class="q-mt-md" />
                <div class="flex items-start q-mt-md no-wrap">
                  <q-checkbox v-model="acceptedTerms" />
                  <i18n-t
                    class="q-ml-sm cursor-pointer mt-10"
                    @click="acceptedTerms = !acceptedTerms"
                    keypath="Customer.New-domain.I agree with the terms"
                    tag="span"
                  >
                    <template #termsLink>
                      <a :href="$t('Customer.New-domain.terms url')" target="_blank" @click.stop class="omni-link">{{
                        $t('Customer.New-domain.terms and conditions')
                      }}</a>
                    </template>
                  </i18n-t>
                </div>
              </q-card-section>
            </q-card>
          </q-step>

          <!-- Step 5 or step 6 (order completed) -->
          <q-step
            :name="webhostingAccount === 'new' ? 6 : 5"
            :title="$t('Customer.New-domain.Order completed')"
            icon="check"
            :color="step === (webhostingAccount === 'new' ? 6 : 5) ? 'primary' : undefined"
          >
            <q-card class="rounded-borders q-ml-sm max-width-px-600" bordered flat>
              <q-card-section class="q-px-sm q-px-md-lg q-py-lg text-center flex column">
                <!-- Order success -->
                <div class="flex flex-center q-mb-lg" v-if="newDomainSuccess && (newWebhostingSuccess === true || newWebhostingSuccess === null)">
                  <q-icon name="check_circle" class="text-primary q-mr-sm" size="30px" />
                  <span class="text-primary font-size-rem-14">{{ $t('Customer.New-domain.Order completed') }}</span>
                </div>
                <!-- Order partially success -->
                <div
                  class="flex flex-center q-mb-lg"
                  v-if="(newWebhostingSuccess === false && newDomainSuccess === true) || (newWebhostingSuccess === true && newDomainSuccess === false)"
                >
                  <q-icon name="check_circle" class="text-primary q-mr-sm" size="30px" />
                  <span class="text-primary font-size-rem-14">{{ $t('Customer.New-domain.Order partially completed') }}</span>
                </div>
                <!-- Order failed -->
                <div class="flex flex-center q-mb-lg" v-if="newDomainSuccess === false && (newWebhostingSuccess === false || newWebhostingSuccess === null)">
                  <q-icon name="cancel" class="text-negative q-mr-sm" size="30px" />
                  <span class="text-negative font-size-rem-14">{{ $t('Customer.New-domain.Order failed') }}</span>
                </div>

                <!-- Order success or partially success-->
                <p class="q-mt-sm font-size-rem-11" v-if="newDomainSuccess === true && (newWebhostingSuccess === true || newWebhostingSuccess === null)">
                  {{ $t('Customer.New-domain.Thank you for your order') }}
                </p>

                <!-- Domain head -->
                <!-- Show this heading only when a new webhosting account was ordered together with the domain.
                     In that case, newWebhostingSuccess will be true or false (never null).
                     If no new hosting was ordered, we skip this block. -->
                <span class="q-mt-md font-size-rem-13" v-if="newWebhostingSuccess !== null && webhostingAccount === 'new'"
                  >{{ newDomainName }}.{{ newDomainSelectedExtension }}</span
                >

                <!-- New domain available -->
                <p class="q-mt-sm font-size-rem-11" v-if="newDomainSuccess">
                  {{ $t('Customer.New-domain.Your domain registration is in progress') }}
                </p>
                <!-- New domain failed -->
                <p class="q-mt-sm font-size-rem-11" v-else>
                  {{ $t('Customer.New-domain.Something went wrong with the domain order') }}
                </p>

                <!-- Webhosting head -->
                <span class="q-mt-md font-size-rem-13" v-if="newWebhostingSuccess !== null && webhostingAccount === 'new'">{{
                  newWebhostingPackage.name
                }}</span>

                <!-- New webhosting available -->
                <p class="q-mt-sm font-size-rem-11" v-if="newWebhostingSuccess !== null && newWebhostingSuccess === true && webhostingAccount === 'new'">
                  {{ $t('Customer.New-domain.Your new web hosting account is now available') }}
                </p>
                <!-- New webhosting failed -->
                <p class="q-mt-sm font-size-rem-11" v-if="newWebhostingSuccess !== null && newWebhostingSuccess === false && webhostingAccount === 'new'">
                  {{ $t('Customer.New-domain.Something went wrong with the order of the web hosting account') }}
                </p>

                <!-- Action buttons -->
                <div class="row q-mt-xl">
                  <div class="flex col-12 col-md-6 justify-center justify-md-start">
                    <q-btn
                      class="width-px-250"
                      color="primary"
                      :label="$t('Customer.New-domain.Back to domains')"
                      dense
                      outline
                      @click="$router.push({ name: 'domains' })"
                    />
                  </div>
                  <div class="flex col-12 col-md-6 q-mt-md q-mt-md-none q-pl-none q-pl-md-md justify-center justify-md-end">
                    <q-btn class="width-px-250" color="primary" :label="$t('Customer.New-domain.New domain')" dense @click="reloadComponent" />
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </q-step>

          <!-- Buttons -->
          <template #navigation>
            <q-stepper-navigation class="q-pa-none">
              <q-toolbar class="q-pa-none">
                <q-btn
                  color="negative"
                  :label="$t('Shared.Globals.Cancel')"
                  @click="router.push({ name: 'domains' })"
                  v-if="step < (webhostingAccount === 'new' ? 6 : 5)"
                  :disable="isSubmitted"
                />
                <q-toolbar-title></q-toolbar-title>
                <q-btn
                  flat
                  color="primary"
                  :label="$t('Shared.Globals.Back')"
                  class="q-mr-xs"
                  @click="$refs.stepper.previous()"
                  v-if="step > 1 && step < (webhostingAccount === 'new' ? 6 : 5)"
                  :disable="isSubmitted"
                />
                <q-btn
                  v-if="(webhostingAccount === 'new' && step < 5) || (webhostingAccount !== 'new' && step < 4)"
                  :disable="
                    (step === 1 && newDomainIsDomainFree === null) ||
                    (step === 1 &&
                      newDomainIsDomainFree === false &&
                      (!newDomainSelectedExtension || !newDomainName || !newDomainTransferCode || !newDomainIsDomainTransferable)) ||
                    (step === 1 && newDomainIsDomainFree === true && (!newDomainSelectedExtension || !newDomainName)) ||
                    (step === 2 &&
                      (!newDomainOwnerContact ||
                        (!newDomainTechnicalContact && newDomainSelectedProduct.domainTldSettingInfo?.hasTechContact) ||
                        (!newDomainAdministrativeContact && newDomainSelectedProduct.domainTldSettingInfo?.hasAdminContact) ||
                        (!newDomainBillingContact && newDomainSelectedProduct.domainTldSettingInfo?.hasBillingContact))) ||
                    (step === 3 && !webhostingAccount) ||
                    (step === 4 && !newWebhostingPackage)
                  "
                  color="primary"
                  :label="$t('Shared.Globals.Next')"
                  @click="$refs.stepper.next()"
                />
                <q-btn
                  v-if="(webhostingAccount === 'new' && step === 5) || (webhostingAccount !== 'new' && step === 4)"
                  color="primary"
                  :loading="isSubmitted"
                  :disable="isSubmitted || !acceptedTerms"
                  :label="$t('Customer.New-domain.Order')"
                  @click="onSubmit"
                />
              </q-toolbar>
            </q-stepper-navigation>
          </template>
        </q-stepper>
      </q-card-section>
    </q-card>
  </div>
</template>

<style scoped lang="scss"></style>
